import React, { useState } from 'react';
import { Link, navigate } from 'gatsby';
import { Stack } from '../../../atoms/stack/Stack';
import { Row } from '../../../atoms/row/Row';
import { Typography } from '../../../atoms/typography/Typography';
import StarRating from '../../../molecules/shared/starRating/StarRating';
import { Mobile } from '../../../../../static/icons/mobile';
import { Cloud } from '../../../../../static/icons/cloud';
// import { Puzzle } from '../../../../../static/icons/puzzle';
import { LinkButton } from '../../../atoms/linkButton/LinkButton';
import SystemCardBubble from '../../../molecules/category/systemCardBubble/SystemCardBubble';
import {
  getSizeRange,
  isCustomer,
  isCustomerOrAffiliate,
} from '../../../../utils/utils';
import { Open } from '../../../../../static/icons/open';

const LinkInOrOut = ({
  areCustomer,
  externalUrl,
  url,
  category,
  systemName,
  systemId,
  followLink = false,
  locale,
  trackOutboundLink,
  t,
}) => {
  if (
    areCustomer &&
    externalUrl &&
    locale === 'sv_SE' &&
    category === 'ACCOUNTING'
  ) {
    return (
      <LinkButton
        className="w-fit border border-customGray-300 gap-1 text-clamp-button !text-customGray-900"
        variant="transparent"
        textTransform="uppercase"
        aria-label={t('systemCard.websiteLabel', {
          systemName,
        })}
        title={t('systemCard.websiteLabel', {
          systemName,
        })}
        href={externalUrl}
        external
        trackingFn={(e) => {
          e.stopPropagation();
          trackOutboundLink(
            {},
            externalUrl,
            category,
            'industry-page',
            systemId,
            {
              page_type: 'Industry page',
              system_name: systemName,
              result_position: 'card',
            }
          );
        }}
        rel={`noopener ${followLink ? '' : 'nofollow'}`}
      >
        {t('systemCard.website')} <Open size={20} />
      </LinkButton>
    );
  }

  return (
    <LinkButton
      className="w-fit border border-customGray-300 text-clamp-button !text-customGray-900"
      variant="transparent"
      textTransform="uppercase"
      label={t('systemCard.readMore')}
      aria-label={t('systemCard.readMoreLabel', {
        systemName,
      })}
      title={t('systemCard.readMoreLabel', {
        systemName,
      })}
      onClick={() => {}}
    />
  );
};

export const SystemCard = ({
  data,
  type,
  systemPageUrl,
  compareSystems,
  onSystemCompare,
  showCompareBtn,
  category,
  locale,
  trackOutboundLink,
  t,
}) => {
  const [popoverItem, setPopoverItem] = useState({
    item: null,
    position: null,
  });

  const {
    id,
    defaultCategoryNice: systemDefaultCategory,
    defaultCategoryUrl: systemDefaultCategoryUrl,
    systemSiteDataForLocale,
    deployment: deploymentData,
    mobileApps,
    clientCompanySize,
    followLink,
  } = data;
  const {
    systemName,
    systemNameUrl,
    systemExternalUrl,
    extraBtnLink,
    introDescription,
    introDescriptionWhat,
    customerType,
  } = systemSiteDataForLocale;

  const mobileAppFeature = !!mobileApps?.length;
  const deploymentFeature = deploymentData.includes('SAAS');
  const companySizeFeature = getSizeRange(clientCompanySize);

  const isCompared = !!compareSystems?.find((el) => el.id === id);
  const isCompareDisabled = !isCompared && compareSystems?.length === 2;

  const features = [
    {
      key: 'mobile',
      title: t('systemCard.mobileApp'),
      description: t('systemCard.mobileAppPopoverText'),
      icon: <Mobile />,
      show: mobileAppFeature,
    },
    {
      key: 'deployment',
      title: t('systemCard.cloud'),
      description: t('systemCard.cloudPopoverText'),
      icon: <Cloud />,
      show: deploymentFeature,
    },
    {
      key: 'companySize',
      title: t('systemCard.companySize'),
      description: t('systemCard.companySizePopoverText'),
      icon: companySizeFeature,
      show: true,
    },
  ].filter((el) => el.show);

  const mobile = features.find((el) => el.key === 'mobile') !== undefined;
  const deployment =
    features.find((el) => el.key === 'deployment') !== undefined;

  const generatePosition = (item) => {
    if (popoverItem.item?.key === item.key) {
      setPopoverItem({ item: null, position: null });
      return;
    }
    let pos = 12;
    if (item.key === 'deployment') {
      if (mobile) {
        pos = 64;
      }
    }
    if (item.key === 'companySize') {
      if (mobile && deployment) {
        pos = 116;
      }
      if (!mobile || !deployment) {
        pos = 64;
      }
    }
    setPopoverItem({ item, position: pos });
  };

  const url = `${systemPageUrl}${systemNameUrl}/`;

  let text = '';
  if (isCustomer(customerType)) {
    text = introDescription || introDescriptionWhat;
  } else {
    text = introDescriptionWhat || introDescription;
  }

  const introText = text?.length > 400 ? `${text.substring(0, 400)}...` : text;

  return (
    <div
      role="button"
      tabIndex="0"
      onClick={() => {
        navigate(url);
      }}
      onKeyUp={() => {}}
      className="w-full"
    >
      <Stack className="w-full min-w-[220px] h-full xl:max-w-sm mx-auto justify-between bg-white rounded-2xl shadow-md p-4">
        <Stack className="flex-1">
          <Row className="w-full items-center gap-4 border-b border-customGray-200 py-2">
            <img
              src={`https://ik.imagekit.io/businesswith/tr:w-60,h-60,cm-pad_resize,dpr-2/logo/${data.logo.url}`}
              alt={data.logoAlt}
              width={60}
              height={60}
              loading="lazy"
            />
            <Stack className="flex-1 h-full items-start justify-center gap-1 max-h-20">
              <Link to={url}>
                <Typography
                  className="overflow-hidden text-lg leading-none pb-1"
                  textAlign="left"
                  // fontVariant="h7"
                >
                  {systemName}
                </Typography>
              </Link>
              <StarRating
                width={12}
                height={12}
                fillColor="#FEC84B"
                rating={data.ratingBW}
              />
            </Stack>
          </Row>
          {type === 'INDUSTRY' && (
            <Row className="py-2">
              <Typography
                className="py-1 px-2 font-bold bg-primary-100 rounded-md !text-customGray-500"
                textAlign="left"
                fontVariant="sm"
              >
                {systemDefaultCategory}
              </Typography>
            </Row>
          )}
          <Stack className="gap-0">
            {popoverItem.item ? (
              <SystemCardBubble
                position={popoverItem.position}
                title={popoverItem.item.title}
                description={popoverItem.item.description}
                closePopover={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPopoverItem({ item: null, position: null });
                }}
              />
            ) : (
              <div className="my-2 md:h-[240px]">
                <Typography
                  fontVariant="sm"
                  className="!text-customGray-500"
                  textAlign="left"
                >
                  {introText}
                  <Typography
                    as="span"
                    fontVariant="sm"
                    className="!text-primary-700 ml-1 global-hover"
                  >
                    {t('systemCard.learnMore', { systemName })}
                  </Typography>
                </Typography>
              </div>
            )}
          </Stack>
          <Stack className="flex-1 justify-end">
            <Row className="py-4 gap-3">
              {features.map((feature) => (
                <button
                  key={feature.key}
                  type="button"
                  aria-label={feature.title}
                  className="global-hover font-medium !text-primary-700"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    generatePosition(feature);
                  }}
                >
                  {feature.icon}
                </button>
              ))}
            </Row>
            <Row className="sm:justify-start md:justify-between">
              {showCompareBtn && (
                <button
                  type="button"
                  disabled={!isCompared && compareSystems?.length === 2}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSystemCompare({
                      id,
                      systemName,
                      systemUrl: systemNameUrl,
                    });
                  }}
                  className={`w-fit text-clamp-button px-6 py-3 uppercase select-none !text-white ${
                    isCompared ? 'bg-[#FEC84B]' : 'bg-primary-700'
                  } rounded-[48px] ${
                    isCompareDisabled
                      ? 'opacity-50 pointer-events-none'
                      : 'global-hover'
                  }`}
                >
                  {isCompared
                    ? t('comparePreview.remove')
                    : t('systemCard.compare')}
                </button>
              )}
              {type === 'INDUSTRY' &&
                isCustomer(customerType) &&
                (systemExternalUrl || extraBtnLink) && (
                  <LinkButton
                    type="button"
                    href={systemExternalUrl || extraBtnLink}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    trackingFn={(e) => {
                      e.stopPropagation();
                      trackOutboundLink(
                        {},
                        systemExternalUrl || extraBtnLink,
                        category,
                        'industry-page',
                        id,
                        {
                          page_type: 'Industry page',
                          system_name: systemName,
                          result_position: 'card',
                        }
                      );
                    }}
                    external
                    className="gap-1 w-fit text-clamp-button px-6 py-3 uppercase select-none !text-white bg-primary-700 rounded-[48px] global-hover"
                  >
                    {t('systemCard.website')} <Open size={16} />
                  </LinkButton>
                )}
              {type === 'INDUSTRY' && !isCustomer(customerType) && (
                <LinkButton
                  type="button"
                  href={systemDefaultCategoryUrl}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="w-fit text-clamp-button px-6 py-3 uppercase select-none !text-white bg-primary-700 rounded-[48px] global-hover"
                >
                  {t('common.category')}
                </LinkButton>
              )}
              <LinkInOrOut
                areCustomer={isCustomerOrAffiliate(customerType)}
                externalUrl={
                  followLink ? extraBtnLink : systemExternalUrl || extraBtnLink
                }
                category={category}
                systemName={systemName}
                systemId={id}
                locale={locale}
                followLink={followLink}
                trackOutboundLink={trackOutboundLink}
                url={url}
                t={t}
              />
            </Row>
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
};
